import { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { ACdetails, CustomReportsDetails, useCampaignContext } from './CampaignContext';
import { useCustomReportId } from 'customHooks/useParameters';
import {
  DashboardTypes,
  PublishObject,
  PublishOutputLocation_v2Mutation,
  PublishOutputLocationMutationVariables,
  SectionGridLayout,
  UnPublishCampaignMutation,
  UnPublishCampaignMutationVariables,
  GridLayout,
} from 'API';
import {
  // useCustomReportsGetDataSources,
  useDateReportsCampaigns,
  useGetCustomReportSections,
} from 'components/NewPortal/CustomReports/CustomReportsHooks/useCustomReportsGrid';
import { fullSources, useDataSources } from 'customHooks/useDataSources';
import { API } from 'aws-amplify';
import { publishOutputLocation_v2, unPublishCampaign } from 'graphql/mutations';
import APIContext from './APIContext';

type CustomReportContextProps = {
  customReportID: string;
  setCustomReportID: (id: string) => void;
  sectionLayoutState: SectionGridLayout[]; // Updated type
  setSectionLayoutState: React.Dispatch<React.SetStateAction<SectionGridLayout[]>>;
  safeSectionLayoutUpdate: (updater: (prev: SectionGridLayout[]) => SectionGridLayout[]) => void;
  fetchCustomReportSections: (reportID: number | undefined, section?: string) => Promise<SectionGridLayout[] | null>;
  activeCustomReport: CustomReportsDetails; // Adjust type as needed
  dataSources: fullSources[];
  transformedACDetailsReport: ACdetails;

  tableDataCache: Record<string, any[] | null>;
  cacheTableData: (sectionItemID: string, data: any[] | null) => void;
  getCachedTableData: (sectionItemID: string) => any[] | null;
  clearTableDataCache: (sectionItemID?: string) => void;
  registerCleanupFunction: (id: string, cleanupFn: () => void) => void;
  unregisterCleanupFunction: (id: string) => void;
  customReportCampaignList: string[];
  publishPlaceholderObject: Record<string, PublishedObject>;
  setPublishPlaceholderObject: React.Dispatch<React.SetStateAction<Record<string, PublishedObject>>>;
  handlePublishCustomReport: () => Promise<void>;
  handleUnPublishCustomReport: () => Promise<void>;
};

type PublishedObject = {
  position: string;
  outputlocation?: string;
  outputlocationTotal?: string;
  outputlocation_cc?: string;
  outputlocationTotal_cc?: string;
  title?: string;
};

export const CustomReportContext = createContext<CustomReportContextProps | undefined>(undefined);

export const CustomReportContextProvider = (props: any) => {
  const { customReportsList, setCustomReportsList } = useCampaignContext();
  const { getEnvironment } = useContext(APIContext);
  const [customReportID, ,] = useCustomReportId();
  const [fetchCustomReportSections] = useGetCustomReportSections();
  const [publishPlaceholderObject, setPublishPlaceholderObject] = useState<Record<string, PublishedObject>>({});

  const activeCustomReport = customReportsList?.find((report) => report?.CustomReportID === Number(customReportID));
  useEffect(() => {
    console.log('Active Custom Report:', activeCustomReport);
  }, [activeCustomReport]);
  const [transformedACDetailsReport, setTransformedACDetailsReport] = useState<ACdetails>();
  const [getDateReportsCampaigns] = useDateReportsCampaigns();
  const [customReportCampaignList, setCustomReportCampaignList] = useState<string[]>([]);
  const [dataSources] = useDataSources(transformedACDetailsReport, customReportCampaignList);

  const [sectionLayoutState, setSectionLayoutState] = useState<SectionGridLayout[]>([]); // Updated type

  // Centralized cleanup function for section elements
  const cleanupSectionElements = useCallback((elements: Array<GridLayout | null>) => {
    if (!elements || elements.length === 0) return [];

    // Remove null elements
    const nonNullElements = elements.filter((el): el is GridLayout => el !== null);

    // Remove duplicates based on GridPosition.i and prioritize elements with SectionItemID
    const uniqueElements = new Map<string, GridLayout>();
    const seenPositions = new Set<string>();

    // Sort to prioritize elements with SectionItemID
    const sortedElements = [...nonNullElements].sort((a, b) => {
      if (a.SectionItemID && !b.SectionItemID) return -1;
      if (!a.SectionItemID && b.SectionItemID) return 1;
      return 0;
    });

    for (const element of sortedElements) {
      if (!element.GridPosition?.i) continue;

      const positionKey = `${element.GridPosition.x}-${element.GridPosition.y}`;
      const gridId = element.GridPosition.i;

      // Skip if we already have this grid ID and the existing one has SectionItemID
      if (uniqueElements.has(gridId)) {
        const existing = uniqueElements.get(gridId);
        if (existing?.SectionItemID && !element.SectionItemID) {
          continue; // Keep the existing one with SectionItemID
        }
      }

      // Skip if position is already occupied by an element with SectionItemID
      if (seenPositions.has(positionKey)) {
        const existingAtPosition = Array.from(uniqueElements.values()).find(
          (el) => el.GridPosition && `${el.GridPosition.x}-${el.GridPosition.y}` === positionKey,
        );
        if (existingAtPosition?.SectionItemID && !element.SectionItemID) {
          continue;
        }
      }

      // Validate grid position bounds (8x8 grid)
      if (
        element.GridPosition?.x !== null &&
        element.GridPosition?.x !== undefined &&
        element.GridPosition.x >= 0 &&
        element.GridPosition?.x < 8 &&
        element.GridPosition?.y !== null &&
        element.GridPosition?.y !== undefined &&
        element.GridPosition.y >= 0 &&
        element.GridPosition?.y < 8 &&
        element.GridPosition?.w !== null &&
        element.GridPosition?.w !== undefined &&
        element.GridPosition?.h !== null &&
        element.GridPosition?.h !== undefined &&
        element.GridPosition.x + element.GridPosition.w <= 8 &&
        element.GridPosition.y + element.GridPosition.h <= 8
      ) {
        uniqueElements.set(gridId, element);
        seenPositions.add(positionKey);
      }
    }

    return Array.from(uniqueElements.values()).slice(0, 64); // Enforce max 64 elements
  }, []);

  // Cleanup function for sections to prevent duplicate positions and section keys
  const cleanupSections = useCallback((sections: SectionGridLayout[]) => {
    if (!sections || sections.length === 0) return [];

    // Remove duplicates based on Position and SectionPosition.i
    const uniqueSections = new Map<number, SectionGridLayout>();
    const seenSectionKeys = new Set<string>();

    // Sort to prioritize sections with SectionElements
    const sortedSections = [...sections].sort((a, b) => {
      const aHasElements = a.SectionElements && a.SectionElements.length > 0;
      const bHasElements = b.SectionElements && b.SectionElements.length > 0;
      if (aHasElements && !bHasElements) return -1;
      if (!aHasElements && bHasElements) return 1;
      return 0;
    });

    for (const section of sortedSections) {
      const position = section.Position;
      const sectionKey = section.SectionPosition?.i;

      if (!sectionKey || position === null || position === undefined) continue;

      // Skip if we already have this position and the existing one has elements
      if (uniqueSections.has(position)) {
        const existing = uniqueSections.get(position);
        // Prioritize section with elements
        if (existing?.SectionElements?.length && !section.SectionElements?.length) {
          continue;
        }
      }

      // Skip if section key already seen
      if (seenSectionKeys.has(sectionKey)) continue;

      uniqueSections.set(position, section);
      seenSectionKeys.add(sectionKey);
    }

    return Array.from(uniqueSections.values()).sort((a, b) => {
      const aPos = a.Position ?? 0;
      const bPos = b.Position ?? 0;
      return aPos - bPos;
    });
  }, []);

  // Safe section layout state setter with automatic cleanup
  const safeSectionLayoutUpdate = useCallback(
    (updater: (prev: SectionGridLayout[]) => SectionGridLayout[]) => {
      setSectionLayoutState((prevLayout) => {
        const updatedLayout = updater(prevLayout);

        // Apply cleanup to sections themselves first
        const cleanedSections = cleanupSections(updatedLayout);

        // Apply cleanup to elements within each section and deep-clone to avoid shared references
        const cleanedLayout = cleanedSections.map((section) => ({
          ...section,
          SectionElements: (cleanupSectionElements(section.SectionElements || []) || []).map((item) =>
            item
              ? {
                  ...item,
                  GridPosition: item.GridPosition ? { ...item.GridPosition } : item.GridPosition,
                }
              : item,
          ),
        }));

        return cleanedLayout;
      });
    },
    [cleanupSections, cleanupSectionElements],
  );

  // Registry for cleanup functions from child components (using useRef to avoid re-renders)
  const cleanupFunctions = useRef<Record<string, () => void>>({});

  const registerCleanupFunction = useCallback((id: string, cleanupFn: () => void) => {
    cleanupFunctions.current[id] = cleanupFn;
  }, []);

  const unregisterCleanupFunction = useCallback((id: string) => {
    delete cleanupFunctions.current[id];
  }, []);

  // Track previous report ID to detect changes
  const prevReportIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    // Clear cache when report changes (not just when it becomes undefined)
    const currentReportId = activeCustomReport?.CustomReportID;
    if (prevReportIdRef.current !== undefined && prevReportIdRef.current !== currentReportId) {
      // Report changed - clear all cached data
      clearTableDataCache();
      setPublishPlaceholderObject({});
      setSectionLayoutState([]);
    }
    prevReportIdRef.current = currentReportId;

    if (activeCustomReport) {
      if (activeCustomReport.authorized) {
        if (dataSources && dataSources.length > 0) {
          fetchCustomReportSections(activeCustomReport?.CustomReportID).then((response) => {
            if (response) {
              console.log('Fetched Custom Report Sections:', response);
              setSectionLayoutState(response);
            }
          });
        }
      } else {
        fetchCustomReportSections(activeCustomReport?.CustomReportID).then((response) => {
          if (response) {
            setSectionLayoutState(response);
          }
        });
      }
    } else {
      setSectionLayoutState([]);
      clearTableDataCache();
      setPublishPlaceholderObject({}); // Clear published data cache

      // Call all registered cleanup functions
      Object.values(cleanupFunctions.current).forEach((cleanupFn) => cleanupFn());
      cleanupFunctions.current = {}; // Clear cleanup registry
    }
  }, [activeCustomReport, fetchCustomReportSections, dataSources]);

  // new table data state
  const [tableDataCache, setTableDataCache] = useState<Record<string, any[] | null>>({});

  // save data to cache
  const cacheTableData = useCallback((sectionItemID: string, data: any[] | null) => {
    setTableDataCache((prev) => ({
      ...prev,
      [sectionItemID]: data,
    }));
  }, []);

  // Function to get cached table data
  const getCachedTableData = (sectionItemID: string): any[] | null => {
    return tableDataCache[sectionItemID] || null;
  };

  // Funkcija za brisanje cache-a (za jedan ili sve iteme)
  const clearTableDataCache = (sectionItemID?: string) => {
    if (sectionItemID) {
      setTableDataCache((prev) => {
        const newCache = { ...prev };
        delete newCache[sectionItemID];
        return newCache;
      });
    } else {
      setTableDataCache({});
    }
  };

  useEffect(() => {
    let isCancelled = false;

    if (activeCustomReport) {
      // Clear previous data first when switching reports
      setCustomReportCampaignList([]);
      setTransformedACDetailsReport(undefined);

      const setNewACDetails = () => {
        const newAC = {
          __typename: 'flowchartCampaignWithType',
          campaignId: activeCustomReport?.CustomReportID,
          campaign: activeCustomReport?.CustomReportName,
          ClientCountry: activeCustomReport?.Client?.Country,
          ClientCode: activeCustomReport?.Client?.Code,
          startDate: activeCustomReport?.StartDate,
          endDate: activeCustomReport?.EndDate,
          ClientName: '',
          status: '',
          dashboardType: [],
          createdBy: '',
          authorized: activeCustomReport?.authorized,
          subcampaign: {
            [-1]: {
              campaignId: activeCustomReport?.CustomReportID,
              subCampaignId: '-1',
              published: {
                TV: false,
                RADIO: false,
                PRINT: false,
                OOH: false,
                DIGITAL: false,
                CINEMA: false,
                __typename: 'publishedType',
              },
              __typename: 'SubCampaign',
            },
          },
        } as unknown as ACdetails;
        if (!isCancelled) {
          setTransformedACDetailsReport(newAC);
        }
      };

      const findFilterwithColumnCampaigns = activeCustomReport.Filters?.find(
        (filter) => filter?.column === 'campaign_id',
      );
      if (findFilterwithColumnCampaigns) {
        const campaignList = findFilterwithColumnCampaigns.filter?.list;
        setCustomReportCampaignList(campaignList as string[]);
        setNewACDetails();
      } else {
        getDateReportsCampaigns(activeCustomReport).then((campaigns) => {
          // Check if effect was not cancelled (prevents stale data when switching reports quickly)
          if (campaigns && !isCancelled) {
            setCustomReportCampaignList(campaigns);
            setNewACDetails();
          }
        });
      }
    } else {
      // Clear data when no active custom report
      setCustomReportCampaignList([]);
      setTransformedACDetailsReport(undefined);
    }

    // Cleanup function - marks this effect as cancelled when dependencies change
    return () => {
      isCancelled = true;
    };
  }, [activeCustomReport, getDateReportsCampaigns]);

  const handlePublishCustomReport = async () => {
    const publishPlaceholderObjectTurnedIntoArray = Object.entries(publishPlaceholderObject).map(([key, value]) => ({
      ...value,
    }));

    try {
      const publish = (await API.graphql({
        query: publishOutputLocation_v2,
        variables: {
          publishInput: {
            campaignId: activeCustomReport?.CustomReportID.toString(),
            placeholders: publishPlaceholderObjectTurnedIntoArray,
            dashboardType: DashboardTypes.DIGITAL,
            clientCode: activeCustomReport?.Client?.Code,
            clientCountry: activeCustomReport?.Client?.Country,
          } as PublishObject,
          Environment: getEnvironment,
        } as PublishOutputLocationMutationVariables,
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: PublishOutputLocation_v2Mutation };
      if (publish?.data.publishOutputLocation_v2?.includes('Successfully')) {
        setCustomReportsList((prevReports) => {
          return prevReports?.map((report) => {
            if (report?.CustomReportID === activeCustomReport?.CustomReportID) {
              // Provjeri da subcampaign struktura postoji
              if (report?.subcampaign && report.subcampaign[-1]) {
                return {
                  ...report,
                  subcampaign: {
                    ...report.subcampaign,
                    [-1]: {
                      ...report.subcampaign[-1],
                      published: {
                        ...report.subcampaign[-1].published,
                        DIGITAL: true,
                      },
                    },
                  },
                };
              }
            }
            return report;
          });
        });
      }
    } catch (e) {
      console.log('NOT PUBLISHED BCZ', e);
    }
  };

  // console.log('CUSTOMLIST', customReportsList);
  const handleUnPublishCustomReport = async () => {
    try {
      const unpublish = (await API.graphql({
        query: unPublishCampaign,
        variables: {
          campaign_id: activeCustomReport?.CustomReportID.toString(),
          dashboardType: DashboardTypes.DIGITAL,
          clientCode: activeCustomReport?.Client?.Code,
          clientCountry: activeCustomReport?.Client?.Country,
          subCampaignId: '-1',
        } as UnPublishCampaignMutationVariables,
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: UnPublishCampaignMutation };
      if (unpublish?.data.unPublishCampaign?.StatusCode) {
        setCustomReportsList((prevReports) => {
          return prevReports?.map((report) => {
            if (report?.CustomReportID === activeCustomReport?.CustomReportID) {
              // Provjeri da subcampaign struktura postoji
              if (report?.subcampaign && report.subcampaign[-1]) {
                return {
                  ...report,
                  subcampaign: {
                    ...report.subcampaign,
                    [-1]: {
                      ...report.subcampaign[-1],
                      published: {
                        ...report.subcampaign[-1].published,
                        DIGITAL: false,
                      },
                    },
                  },
                };
              }
            }
            return report;
          });
        });
        // alert('Custom report published successfully!');
      }
    } catch (e) {
      console.log('NOT PUBLISHED BCZ', e);
    }
  };

  return (
    <CustomReportContext.Provider
      value={{
        customReportID,
        setCustomReportID: () => {},
        sectionLayoutState,
        setSectionLayoutState,
        safeSectionLayoutUpdate,
        fetchCustomReportSections,
        activeCustomReport,
        dataSources,
        transformedACDetailsReport,
        tableDataCache,
        cacheTableData,
        getCachedTableData,
        clearTableDataCache,
        registerCleanupFunction,
        unregisterCleanupFunction,
        customReportCampaignList,
        publishPlaceholderObject,
        setPublishPlaceholderObject,
        handlePublishCustomReport,
        handleUnPublishCustomReport,
      }}
    >
      {props.children}
    </CustomReportContext.Provider>
  );
};

export const useCustomReportContext = () => {
  const context = useContext(CustomReportContext);
  if (context === undefined) {
    throw new Error('context needs to be provided in CustomReport context');
  }
  return context;
};
